import React, { useState, useEffect } from 'react';
import { MdSearch, MdFileDownload } from 'react-icons/md';
import { formatDate } from '../utils/dateUtils';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import { authFetch } from '../components/utils/authFetch';
import { GET_EXPORT_DATA_API, BASE_API } from '../config/API';
import styles from './ReportLog.module.css';

const ReportLog = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=10`;
            if (debouncedSearch) queryParams += `&search=${debouncedSearch}`;

            const response = await authFetch(
                `${GET_EXPORT_DATA_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setReports(result.data || []);
                const total = result.total || 0;
                const limit = result.limit || 10;
                setTotalRecords(total);
                setTotalPages(Math.ceil(total / limit));
            }
        } catch (error) {
            console.error('Error fetching report logs:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [currentPage, debouncedSearch]);

    const handleDownload = (filePath) => {
        if (!filePath) return;
        const fullUrl = filePath.startsWith('http') ? filePath : `${BASE_API}/${filePath}`;
        window.open(fullUrl, '_blank');
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Report Log</h2>
            </div>

            <div className={styles['card-container']}>
                <div className={styles['search-container']}>
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search reports by type or file name..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className={styles['clear-search-btn']}
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className={styles['empty-state-container']}>
                        <Loader text="Fetching report logs..." />
                    </div>
                ) : reports.length === 0 ? (
                    <div className={styles['empty-state-container']}>
                        <NoData title="No Reports Found" message="Logs will appear here once reports are generated." />
                    </div>
                ) : (
                    <>
                        <div className={styles['table-container']}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Sn</th>
                                        <th>Report Type</th>
                                        <th>File Name</th>
                                        <th>Created Date</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'center' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((report, index) => {
                                        const isCompleted = report.status?.toLowerCase().trim() === 'completed';
                                        return (
                                            <tr key={report.id}>
                                                <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                <td className={styles['report-type']}>
                                                    {report.type?.replace(/_/g, ' ')}
                                                </td>
                                                <td className={styles['file-name']}>{report.file_name}</td>
                                                <td>{formatDate(report.createdAt)}</td>
                                                <td>
                                                    <span className={`${styles['status-badge']} ${isCompleted ? styles['status-completed'] : styles['status-pending']}`}>
                                                        {report.status}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {isCompleted && (
                                                        <button
                                                            className={styles['download-btn']}
                                                            onClick={() => handleDownload(report.file_path)}
                                                        >
                                                            <MdFileDownload /> Download
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles['mobile-card-view']}>
                            {reports.map((report) => {
                                const isCompleted = report.status?.toLowerCase().trim() === 'completed';
                                return (
                                    <div key={report.id} className={styles['dashboard-card']}>
                                        <div className={styles['card-header']}>
                                            <div className={styles['card-title']}>
                                                <strong>{report.type?.replace(/_/g, ' ')}</strong>
                                                <span className={styles['file-name']}>{report.file_name}</span>
                                            </div>
                                            <span className={`${styles['status-badge']} ${isCompleted ? styles['status-completed'] : styles['status-pending']}`}>
                                                {report.status}
                                            </span>
                                        </div>
                                        <div className={styles['card-body']}>
                                            <div className={styles['card-row']}>
                                                <span className={styles['card-label']}>Created At</span>
                                                <span className={styles['card-value']}>{formatDate(report.createdAt)}</span>
                                            </div>
                                            {isCompleted && (
                                                <button
                                                    className={styles['download-btn']}
                                                    onClick={() => handleDownload(report.file_path)}
                                                    style={{ marginTop: '0.5rem', width: '100%', justifyContent: 'center' }}
                                                >
                                                    <MdFileDownload /> Download
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles['pagination-card']}>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalRecords={totalRecords}
                                currentCount={reports.length}
                                onPageChange={(page) => setCurrentPage(page)}
                                entityName="Report Logs"
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReportLog;
